import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
`;

export const Container = styled.div`
    background-color: #F2FFED;
    padding: 6rem 0;
    position: relative;
    overflow: hidden;
`;

export const GradientBlob = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    background: linear-gradient(to left, rgba(187, 247, 208, 0.3) 0%, transparent 100%);
    filter: blur(100px);
`;

export const Content = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 1rem;
    position: relative;
    z-index: 10;

    @media (min-width: 640px) {
        padding: 0 1.5rem;
    }

    @media (min-width: 1024px) {
        padding: 0 2rem;
    }
`;

export const Row = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 3rem;

    @media (min-width: 768px) {
        flex-direction: row;
    }
`;

export const TextColumn = styled.div`
    width: 100%;

    @media (min-width: 768px) {
        width: 50%;
    }
`;

export const Heading = styled.h2`
    font-size: 2.25rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 1.5rem;
    line-height: 1.2;

    @media (min-width: 768px) {
        font-size: 3.75rem;
    }
`;

export const Description = styled.p`
    font-size: 1.125rem;
    color: #374151;
    margin-bottom: 2rem;
    line-height: 1.75;
    max-width: 32rem;

    @media (min-width: 768px) {
        font-size: 1.25rem;
    }
`;

export const Button = styled.button`
    background-color: #000;
    color: white;
    padding: 1rem 2rem;
    border-radius: 9999px;
    font-weight: 700;
    font-size: 1.125rem;
    display: inline-flex;
    align-items: center;
    border: none;
    cursor: pointer;
    transition: background-color 200ms, transform 200ms;

    &:hover {
        background-color: #1f2937;

        svg {
            transform: translateX(0.25rem);
        }
    }

    svg {
        transition: transform 200ms;
    }
`;

export const DashboardColumn = styled.div`
    width: 100%;

    @media (min-width: 768px) {
        width: 50%;
    }
`;

export const DashboardCard = styled.div`
    background-color: white;
    border-radius: 1rem;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    border: 1px solid #f3f4f6;
    padding: 0.5rem;
    transform: rotate(0deg);
    transition: transform 500ms;

    @media (min-width: 768px) {
        padding: 1rem;
        transform: rotate(-2deg);

        &:hover {
            transform: rotate(0deg);
        }
    }
`;

export const DashboardInner = styled.div`
    background-color: #f9fafb;
    border-radius: 0.75rem;
    overflow: hidden;
`;

export const Header = styled.div`
    height: 4rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    padding: 0 1.5rem;
    justify-content: space-between;
    background-color: white;
`;

export const HeaderPulse = styled.div`
    width: 8rem;
    height: 1.5rem;
    background-color: #e5e7eb;
    border-radius: 9999px;
    animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;

export const HeaderButtons = styled.div`
    display: flex;
    gap: 0.5rem;
`;

export const HeaderButton = styled.div`
    width: 5rem;
    height: 2rem;
    background-color: #f3f4f6;
    border-radius: 0.5rem;
`;

export const DashboardGrid = styled.div`
    padding: 1.5rem;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
`;

interface StaffCardProps {
    color: 'blue' | 'pink' | 'gray';
}

const getCardBackground = (color: string) => {
    if (color === 'blue') return 'rgba(219, 234, 254, 0.5)';
    if (color === 'pink') return 'rgba(252, 231, 243, 0.5)';
    return 'white';
};

const getCardBorder = (color: string) => {
    if (color === 'blue') return '#dbeafe';
    if (color === 'pink') return '#fce7f3';
    return '#f3f4f6';
};

export const StaffCard = styled.div<StaffCardProps>`
    background-color: ${props => getCardBackground(props.color)};
    height: 12rem;
    border-radius: 0.5rem;
    border: 1px solid ${props => getCardBorder(props.color)};
    padding: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    opacity: ${props => props.color === 'gray' ? 0.5 : 1};
`;

export const StaffImage = styled.img`
    width: 4rem;
    height: 4rem;
    border-radius: 9999px;
    margin-bottom: 0.5rem;
    object-fit: cover;
`;

interface StaffBarProps {
    color: 'blue' | 'pink';
    width: string;
}

const getBarBackground = (color: string) => {
    if (color === 'blue') return '#bfdbfe';
    if (color === 'pink') return '#f9a8d4';
    return '#e5e7eb';
};

export const StaffBar = styled.div<StaffBarProps>`
    width: ${props => props.width};
    height: ${props => props.width === '3rem' ? '0.75rem' : '1rem'};
    background-color: ${props => getBarBackground(props.color)};
    border-radius: 0.25rem;
    margin-bottom: 0.25rem;
`;

// ============================================
// Feature Sections (WhatsApp & Mercado Pago)
// ============================================

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

interface FeatureSectionProps {
    bgColor?: string;
}

export const FeatureSection = styled.section<FeatureSectionProps>`
    background-color: ${props => props.bgColor || '#F2FFED'};
    padding: 5rem 0;
    position: relative;
    overflow: hidden;
`;

export const FeatureContent = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 1rem;
    position: relative;
    z-index: 10;

    @media (min-width: 640px) {
        padding: 0 1.5rem;
    }

    @media (min-width: 1024px) {
        padding: 0 2rem;
    }
`;

export const FeatureRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 3rem;

    @media (min-width: 768px) {
        flex-direction: row;
    }
`;

export const FeatureRowReversed = styled(FeatureRow)`
    @media (min-width: 768px) {
        flex-direction: row-reverse;
    }
`;

export const FeatureTextColumn = styled.div`
    width: 100%;
    animation: ${fadeInUp} 0.6s ease-out;

    @media (min-width: 768px) {
        width: 50%;
    }
`;

export const FeatureVisualColumn = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    animation: ${fadeInUp} 0.6s ease-out 0.2s both;

    @media (min-width: 768px) {
        width: 50%;
    }
`;

export const FeatureHeading = styled.h3`
    font-size: 1.75rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 1rem;
    line-height: 1.2;

    @media (min-width: 768px) {
        font-size: 2.5rem;
    }
`;

export const FeatureDescription = styled.p`
    font-size: 1rem;
    color: #374151;
    margin-bottom: 1.5rem;
    line-height: 1.75;
    max-width: 28rem;

    @media (min-width: 768px) {
        font-size: 1.125rem;
    }
`;

export const FeatureList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

interface FeatureListItemProps {
    iconColor?: string;
}

export const FeatureListItem = styled.li<FeatureListItemProps>`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1rem;
    color: #374151;
    font-weight: 500;

    svg {
        color: ${props => props.iconColor || '#25D366'};
        flex-shrink: 0;
    }
`;

// ============================================
// WhatsApp Phone Mockup
// ============================================

export const PhoneMockup = styled.div`
    width: 280px;
    background-color: white;
    border-radius: 2rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    padding: 0.5rem;
    transition: transform 300ms, box-shadow 300ms;

    &:hover {
        transform: scale(1.02);
        box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.3);
    }

    @media (max-width: 768px) {
        width: 260px;
    }
`;

export const WhatsAppScreen = styled.div`
    background-color: #ECE5DD;
    border-radius: 1.5rem;
    overflow: hidden;
    min-height: 400px;
`;

export const WhatsAppHeader = styled.div`
    background-color: #075E54;
    color: white;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;

export const WhatsAppAvatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #128C7E;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1rem;
`;

export const WhatsAppContactName = styled.span`
    font-weight: 600;
    font-size: 1rem;
`;

export const WhatsAppMessages = styled.div`
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

export const WhatsAppBubble = styled.div`
    background-color: #DCF8C6;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem 0.5rem 0.5rem 0;
    max-width: 85%;
    align-self: flex-end;
    position: relative;
    font-size: 0.875rem;
    line-height: 1.4;
    color: #111827;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

export const WhatsAppBubbleIncoming = styled(WhatsAppBubble)`
    background-color: white;
    align-self: flex-start;
    border-radius: 0.5rem 0.5rem 0 0.5rem;
`;

export const WhatsAppTime = styled.span`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.25rem;
    font-size: 0.7rem;
    color: #667781;
    margin-top: 0.25rem;

    svg {
        color: #53bdeb;
    }
`;

// ============================================
// Mercado Pago Payment Mockup
// ============================================

export const PaymentCard = styled.div`
    width: 300px;
    background-color: white;
    border-radius: 1rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    transition: transform 300ms, box-shadow 300ms;

    &:hover {
        transform: scale(1.02);
        box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.3);
    }

    @media (max-width: 768px) {
        width: 280px;
    }
`;

export const PaymentHeader = styled.div`
    background-color: #009EE3;
    color: white;
    padding: 1.25rem;
    text-align: center;
    font-weight: 700;
    font-size: 1.125rem;
    letter-spacing: 0.5px;
`;

export const PaymentBody = styled.div`
    padding: 2rem 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
`;

export const PaymentAmount = styled.div`
    font-size: 2.5rem;
    font-weight: 800;
    color: #111827;
`;

export const PaymentSuccess = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    color: #16a34a;
    font-weight: 600;
    font-size: 1rem;

    svg {
        color: #16a34a;
    }
`;

export const PaymentDetails = styled.div`
    width: 100%;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
    margin-top: 0.5rem;
`;

export const PaymentDetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;
    color: #6b7280;
    padding: 0.5rem 0;

    span:last-child {
        color: #111827;
        font-weight: 500;
    }
`;

export const QRCodePlaceholder = styled.div`
    width: 120px;
    height: 120px;
    background: linear-gradient(45deg, #e5e7eb 25%, transparent 25%),
                linear-gradient(-45deg, #e5e7eb 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, #e5e7eb 75%),
                linear-gradient(-45deg, transparent 75%, #e5e7eb 75%);
    background-size: 20px 20px;
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        color: #9ca3af;
    }
`;

// ============================================
// Client Details Mockup
// ============================================

export const ClientDetailCard = styled.div`
    width: 280px;
    background-color: white;
    border-radius: 1rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    transition: transform 300ms, box-shadow 300ms;

    &:hover {
        transform: scale(1.02);
        box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.3);
    }

    @media (max-width: 768px) {
        width: 260px;
    }
`;

export const ClientPhoto = styled.div`
    width: 100%;
    height: 200px;
    background: linear-gradient(135deg, #f8bbd9 0%, #e1bee7 50%, #b39ddb 100%);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 80px;
        height: 80px;
        background: rgba(255, 255, 255, 0.3);
        border-radius: 50%;
    }

    svg {
        color: white;
        opacity: 0.8;
    }
`;

export const ClientPhotoOverlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1rem;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.5));
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
`;

export const ClientPhotoDate = styled.span`
    color: white;
    font-size: 0.875rem;
    font-weight: 500;
`;

export const ClientPhotoCarousel = styled.div`
    display: flex;
    gap: 0.25rem;
`;

export const CarouselDot = styled.div<{ $active?: boolean }>`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
`;

export const ClientInfoSection = styled.div`
    padding: 1.25rem;
`;

export const ClientName = styled.h4`
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 0.75rem 0;
    letter-spacing: 0.5px;
`;

export const FormulaSection = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background-color: #fdf2f8;
    border-radius: 0.5rem;
    border: 1px solid #fce7f3;
`;

export const FormulaIcon = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 0.375rem;
    background-color: #f9a8d4;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        color: white;
        width: 18px;
        height: 18px;
    }
`;

export const FormulaText = styled.span`
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
`;

export const ClientActionRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.75rem;
`;

export const ClientActionButton = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 0.375rem;
    border: 1px solid #e5e7eb;
    background-color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 200ms;

    &:hover {
        background-color: #f9fafb;
    }

    svg {
        color: #6b7280;
        width: 16px;
        height: 16px;
    }
`;

// ============================================
// Store Badges
// ============================================

export const StoreBadges = styled.div`
    display: flex;
    gap: 0.75rem;
    margin-top: 2rem;
    flex-wrap: wrap;
`;

export const StoreBadge = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background-color: #000;
    color: white;
    padding: 0.625rem 1rem;
    border-radius: 0.5rem;
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
    transition: opacity 200ms;

    &:hover {
        opacity: 0.85;
    }

    svg {
        width: 20px;
        height: 20px;
        color: white;
    }
`;

export const StoreBadgeText = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

export const StoreBadgeSmall = styled.span`
    font-size: 0.625rem;
    font-weight: 400;
    opacity: 0.8;
`;

export const StoreBadgeLarge = styled.span`
    font-size: 0.875rem;
    font-weight: 600;
`;
